'use client';

import { postCart, useCartStore } from '@/features/cart/store/cart';
import { handleException } from '@/utils/exception';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { Grid } from '@andritzot/metris-web-ui/layout/grid';
import { useEffect } from 'react';
import { type SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';

import { AgreePrivacyField } from './AgreePrivacyField';
import { CityField } from './CityField';
import { CommentField } from './CommentField';
import { CompanyField } from './CompanyField';
import { ContactMeSwitch } from './ContactMeSwitch';
import { CountryField } from './CountryField';
import { EmailField } from './EmailField';
import { FirstNameField } from './FirstNameField';
import { JobTitleField } from './JobTitleField';
import { LastNameField } from './LastNameField';
import { PhoneField } from './PhoneField';
import { type FormProps } from './types';

interface PriceQuoteFormProps {
  onCanSubmitChange?: (canSubmit: boolean) => void;
}

const PriceQuoteForm = ({ onCanSubmitChange }: PriceQuoteFormProps) => {
  const { control, handleSubmit, reset } = useForm<FormProps>({
    defaultValues: {
      beContacted: true,
      comment: '',
      firstName: '',
      lastName: '',
      email: '',
      company: '',
      jobTitle: '',
      country: null,
      city: '',
      phone: '',
      agreePrivacy: false,
    },
  });
  const agreePrivacy = useWatch({ control, name: 'agreePrivacy' });
  const cartProducts = useCartStore((s) => s.cartProducts);
  const isLoading = useCartStore((s) => s.isLoading);

  useEffect(() => {
    onCanSubmitChange?.(agreePrivacy && !isLoading);
  }, [agreePrivacy, isLoading, onCanSubmitChange]);

  const isCartEmpty = cartProducts.length === 0;

  const onSubmit: SubmitHandler<FormProps> = async (data) => {
    const { city, company, beContacted, country, email, firstName, lastName, jobTitle, phone, comment } = data;
    try {
      await postCart({
        city,
        beContacted,
        company,
        email,
        firstName,
        lastName,
        jobTitle,
        phone,
        country: country?.name ?? '',
        comment,
      });
      toast.success('Request has been sent!', {
        autoClose: false,
      });
      reset();
    } catch (e) {
      toast.error(handleException(e, 'Error sending data to server'), {
        autoClose: false,
      });
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexFlow: 'column' }}>
      <Box sx={{ p: 4, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="cardHeading">Contact details</Typography>
      </Box>

      <Box sx={{ margin: 4, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Box
          component="form"
          id="price-quote-form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}
        >
          {!isCartEmpty && (
            <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 4 }}>
              <ContactMeSwitch control={control} />
            </Box>
          )}
          <Grid container spacing={2} sx={{ pt: 4 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FirstNameField control={control} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <LastNameField control={control} />
            </Grid>
            <Grid size={12} sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}>
              <EmailField control={control} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CompanyField control={control} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <JobTitleField control={control} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CountryField control={control} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CityField control={control} />
            </Grid>
            <Grid size={12} sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
              <PhoneField control={control} />
            </Grid>
            <Grid size={12}>
              <CommentField control={control} isCartEmpty={isCartEmpty} />
            </Grid>
          </Grid>
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 4, pt: 4 }}>
            <AgreePrivacyField control={control} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PriceQuoteForm;
